import { lazy, useEffect } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import { ADMIN_STAFF_ROLES } from './hooks/useStaffRoles';
import { RequireRoles } from './routing/RequireRoles';
import { RequirePack } from './routing/RequirePack';
import { CAPABILITY_PACK } from '@wapharma/domain';
import { recordReproBreadcrumb } from '@wapharma/shared';
import {
  importCatalogListPage,
  importCatalogPage,
  importHomePage,
  importOrdersPage,
  importProcurementPage,
} from './routing/lazyPages';
import { AdminRouteError } from './components/AdminRouteError';
import { AdminShell } from './AdminShell';
import { LoginPage } from './pages/LoginPage';
import { ForcePasswordChangePage } from './pages/ForcePasswordChangePage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { openMessagesFlyout, type MessagesFlyoutDraftReference, type MessagesFlyoutEntityTarget } from './lib/messagesFlyout';

const HomePage = lazy(() => importHomePage().then((m) => ({ default: m.HomePage })));
const CatalogListPage = lazy(importCatalogListPage);
const CatalogPage = lazy(() => importCatalogPage().then((m) => ({ default: m.CatalogPage })));
const InventoryLotsPage = lazy(() =>
  import('./pages/InventoryLotsPage').then((m) => ({ default: m.InventoryLotsPage })),
);
const InventoryLotDetailPage = lazy(() =>
  import('./pages/InventoryLotDetailPage').then((m) => ({ default: m.InventoryLotDetailPage })),
);
const InventoryPage = lazy(() => import('./pages/InventoryPage').then((m) => ({ default: m.InventoryPage })));
const LocationsPage = lazy(() => import('./pages/LocationsPage').then((m) => ({ default: m.LocationsPage })));
const ProductTypesPage = lazy(() =>
  import('./pages/ProductTypesPage').then((m) => ({ default: m.ProductTypesPage })),
);
const ProductTypeDetailPage = lazy(() =>
  import('./pages/ProductTypeDetailPage').then((m) => ({ default: m.ProductTypeDetailPage })),
);
const OptionGroupDetailPage = lazy(() =>
  import('./pages/OptionGroupDetailPage').then((m) => ({ default: m.OptionGroupDetailPage })),
);
const OrdersPage = lazy(() => importOrdersPage().then((m) => ({ default: m.OrdersPage })));
const ProcurementPage = lazy(() => importProcurementPage().then((m) => ({ default: m.ProcurementPage })));
const QuotesPage = lazy(() => import('./pages/QuotesPage').then((m) => ({ default: m.QuotesPage })));
const SaleQuotesPage = lazy(() =>
  import('./pages/SaleQuotesPage').then((m) => ({ default: m.SaleQuotesPage })),
);
const CustomerPage = lazy(() => import('./pages/CustomerPage').then((m) => ({ default: m.CustomerPage })));
const DocumentsPage = lazy(() =>
  import('./pages/DocumentsPage').then((m) => ({ default: m.DocumentsPage })),
);
const SearchPage = lazy(() => import('./pages/SearchPage').then((m) => ({ default: m.SearchPage })));
const UsersPage = lazy(() => import('./pages/UsersPage').then((m) => ({ default: m.UsersPage })));
const RolesPage = lazy(() => import('./pages/RolesPage').then((m) => ({ default: m.RolesPage })));
const RoleDetailPage = lazy(() => import('./pages/RoleDetailPage').then((m) => ({ default: m.RoleDetailPage })));
const FulfillmentSettingsPage = lazy(() =>
  import('./pages/FulfillmentSettingsPage').then((m) => ({ default: m.FulfillmentSettingsPage })),
);
const CatalogReviewsInboxPage = lazy(() =>
  import('./pages/CatalogReviewsInboxPage').then((m) => ({
    default: m.CatalogReviewsInboxPage,
  })),
);
const StorefrontHomeSettingsPage = lazy(() =>
  import('./pages/StorefrontHomeSettingsPage').then((m) => ({
    default: m.StorefrontHomeSettingsPage,
  })),
);
const StorefrontSiteSettingsPage = lazy(() =>
  import('./pages/StorefrontSiteSettingsPage').then((m) => ({
    default: m.StorefrontSiteSettingsPage,
  })),
);
const DiscountCodesPage = lazy(() =>
  import('./pages/DiscountCodesPage').then((m) => ({ default: m.DiscountCodesPage })),
);
const LaunchReadinessPage = lazy(() =>
  import('./pages/LaunchReadinessPage').then((m) => ({ default: m.LaunchReadinessPage })),
);
const ExternalCatalogDataSourcePage = lazy(() =>
  import('./pages/ExternalCatalogDataSourcePage').then((m) => ({ default: m.ExternalCatalogDataSourcePage })),
);
const NotFoundPage = lazy(() => import('./pages/NotFoundPage').then((m) => ({ default: m.NotFoundPage })));
const PosSessionsPage = lazy(() =>
  import('./pages/PosSessionsPage').then((m) => ({ default: m.PosSessionsPage })),
);
const PosSessionDetailPage = lazy(() =>
  import('./pages/PosSessionDetailPage').then((m) => ({ default: m.PosSessionDetailPage })),
);
const AccountingPage = lazy(() =>
  import('./pages/AccountingPage').then((m) => ({ default: m.AccountingPage })),
);
const LeadsPage = lazy(() => import('./pages/LeadsPage').then((m) => ({ default: m.LeadsPage })));

/** Old `/catalog/images` bookmarks → catalog list (media on product + taxonomy screens). */
function CatalogImagesRedirect() {
  return <Navigate to="/catalog" replace />;
}

/** Old `/catalog/legacy` bookmarks → new list route (query preserved). */
function CatalogLegacyRedirect() {
  const [searchParams] = useSearchParams();
  const q = searchParams.toString();
  return <Navigate to={q ? `/catalog?${q}` : '/catalog'} replace />;
}
const LeadFlowsPage = lazy(() =>
  import('./pages/LeadFlowsPage').then((m) => ({ default: m.LeadFlowsPage })),
);
const LeadFlowDetailPage = lazy(() =>
  import('./pages/LeadFlowDetailPage').then((m) => ({ default: m.LeadFlowDetailPage })),
);
const ArticlesAdminPage = lazy(() =>
  import('./pages/ArticlesAdminPage').then((m) => ({ default: m.ArticlesAdminPage })),
);
const DesignSystemPlaygroundPage = lazy(() =>
  import('./pages/DesignSystemPlaygroundPage').then((m) => ({ default: m.DesignSystemPlaygroundPage })),
);
const SupportTicketsPage = lazy(() =>
  import('./pages/SupportTicketsPage').then((m) => ({ default: m.SupportTicketsPage })),
);
function MessagesRouteRedirect() {
  const navigate = useNavigate();
  const location = useLocation();
  const { conversationId } = useParams<{ conversationId?: string }>();

  useEffect(() => {
    const state = (location.state as {
      openEntity?: MessagesFlyoutEntityTarget;
      draftReference?: MessagesFlyoutDraftReference;
      draftReferences?: MessagesFlyoutDraftReference[];
    } | null) ?? null;
    openMessagesFlyout({
      conversationId: conversationId ?? null,
      openEntity: state?.openEntity ?? null,
      draftReference: state?.draftReference ?? null,
      draftReferences: state?.draftReferences ?? null,
    });
    navigate('/', { replace: true });
  }, [conversationId, location.state, navigate]);

  return null;
}

function captureUiBreadcrumbFromEvent(e: Event): void {
  if (typeof window === 'undefined') return;
  const target = e.target;
  if (!(target instanceof HTMLElement)) return;
  const tag = target.tagName.toLowerCase();
  if (tag === 'html' || tag === 'body') return;

  const el = target.closest('a,button,[role=\"button\"],[data-testid],input,select,textarea') ?? target;
  if (!(el instanceof HTMLElement)) return;

  const label =
    el.getAttribute('aria-label')?.trim() ||
    el.getAttribute('title')?.trim() ||
    el.textContent?.trim().replace(/\\s+/g, ' ').slice(0, 120) ||
    tag;
  if (!label) return;

  const testId = el.getAttribute('data-testid')?.trim();
  const id = el.id?.trim();
  const role = el.getAttribute('role')?.trim() || undefined;
  const href =
    el instanceof HTMLAnchorElement ? el.getAttribute('href')?.trim() || undefined : undefined;
  const selector = testId
    ? `[data-testid=\"${testId}\"]`
    : id
      ? `#${id}`
      : role
        ? `${el.tagName.toLowerCase()}[role=\"${role}\"]`
        : el.tagName.toLowerCase();

  recordReproBreadcrumb({ t: 'ui', label, selector, href, role });
}

export function App() {
  const location = useLocation();

  useEffect(() => {
    if (!import.meta.env.DEV) {
      recordReproBreadcrumb({ t: 'route', path: location.pathname + location.search });
    }
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    const captureOpts: AddEventListenerOptions = { capture: true };
    const onClick = (e: MouseEvent) => captureUiBreadcrumbFromEvent(e);
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      captureUiBreadcrumbFromEvent(e);
    };
    window.addEventListener('click', onClick, captureOpts);
    window.addEventListener('keydown', onKeyDown, captureOpts);
    return () => {
      window.removeEventListener('click', onClick, captureOpts);
      window.removeEventListener('keydown', onKeyDown, captureOpts);
    };
  }, []);

  return (
    <AuthProvider>
      <ToastProvider>
        <>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/force-password-change" element={<ForcePasswordChangePage />} />
            <Route element={<AdminShell />} errorElement={<AdminRouteError />}>
              <Route element={<RequireRoles anyOf={[...ADMIN_STAFF_ROLES]} />}>
                <Route index element={<HomePage />} />
                <Route path="catalog/images" element={<CatalogImagesRedirect />} />
                <Route path="catalog/products/:productId" element={<CatalogPage />} />
                <Route path="catalog/legacy" element={<CatalogLegacyRedirect />} />
                <Route path="catalog/types/:typeCode" element={<ProductTypeDetailPage />} />
                <Route path="catalog/types" element={<ProductTypesPage />} />
                <Route path="catalog/option-groups/:groupId" element={<OptionGroupDetailPage />} />
                <Route path="catalog" element={<CatalogListPage />} />
                <Route path="catalog/reviews" element={<CatalogReviewsInboxPage />} />
                <Route path="orders/:orderId?" element={<OrdersPage />} />
                <Route path="messages/:conversationId?" element={<MessagesRouteRedirect />} />
                <Route path="sales/quotes/:id" element={<SaleQuotesPage />} />
                <Route path="sales/quotes" element={<SaleQuotesPage />} />
                <Route path="documents" element={<DocumentsPage />} />
                <Route path="search" element={<SearchPage />} />
                <Route path="stock" element={<Navigate to="/inventory" replace />} />
                <Route path="stock/*" element={<Navigate to="/inventory" replace />} />
                <Route element={<RequireRoles anyOf={['SUPER_ADMIN', 'INVENTORY', 'VIEWER']} />}>
                  <Route path="inventory/lots/:batchId" element={<InventoryLotDetailPage />} />
                  <Route path="inventory/lots" element={<InventoryLotsPage />} />
                  <Route path="inventory/locations" element={<LocationsPage />} />
                  <Route path="inventory" element={<InventoryPage />} />
                  <Route path="procurement/quotes/:quoteId" element={<QuotesPage />} />
                  <Route path="procurement/quotes" element={<QuotesPage />} />
                  <Route path="procurement/purchase-orders/:purchaseOrderId" element={<ProcurementPage />} />
                  <Route path="procurement/purchase-orders" element={<ProcurementPage />} />
                  <Route path="procurement/suppliers/:supplierId" element={<ProcurementPage />} />
                  <Route path="procurement/suppliers" element={<ProcurementPage />} />
                  <Route path="procurement/payments" element={<Navigate to="/procurement/suppliers" replace />} />
                  <Route path="procurement" element={<Navigate to="/procurement/purchase-orders" replace />} />
                </Route>
                <Route element={<RequireRoles anyOf={['SUPER_ADMIN', 'SALES', 'VIEWER']} />}>
                  <Route path="customer/demands/:demandId" element={<CustomerPage />} />
                  <Route path="customer/profiles/:profileId" element={<CustomerPage />} />
                  <Route path="customer/legal-entities/:legalEntityId" element={<CustomerPage />} />
                  <Route path="customer" element={<CustomerPage />} />
                </Route>
                <Route element={<RequireRoles anyOf={['SUPER_ADMIN', 'ACCOUNTING']} />}>
                  <Route path="accounting" element={<AccountingPage />} />
                </Route>
                <Route element={<RequireRoles anyOf={['SUPER_ADMIN', 'SALES']} />}>
                  <Route path="pos/sessions/:sessionId" element={<PosSessionDetailPage />} />
                  <Route path="pos/sessions" element={<PosSessionsPage />} />
                  <Route path="settings/launch-readiness" element={<LaunchReadinessPage />} />
                </Route>
                <Route element={<RequirePack anyOf={[CAPABILITY_PACK.CRM_LEAD]} />}>
                  <Route path="crm/flows/:id" element={<LeadFlowDetailPage />} />
                  <Route path="crm/flows" element={<LeadFlowsPage />} />
                  <Route path="leads/:leadId" element={<LeadsPage />} />
                  <Route path="leads" element={<LeadsPage />} />
                  <Route path="support/:ticketId" element={<SupportTicketsPage />} />
                  <Route path="support" element={<SupportTicketsPage />} />
                </Route>
                <Route element={<RequirePack anyOf={[CAPABILITY_PACK.CONTENT_PUBLISH]} />}>
                  <Route path="content/articles" element={<ArticlesAdminPage />} />
                </Route>
                <Route element={<RequirePack anyOf={[CAPABILITY_PACK.STOREFRONT_MAINTAIN]} />}>
                  <Route path="settings/storefront-home" element={<StorefrontHomeSettingsPage />} />
                  <Route path="settings/storefront-site" element={<StorefrontSiteSettingsPage />} />
                </Route>
                <Route element={<RequirePack anyOf={[CAPABILITY_PACK.STOREFRONT_COMMERCE]} />}>
                  <Route path="settings/shipping" element={<FulfillmentSettingsPage mode="shipping" />} />
                  <Route path="settings/payment" element={<FulfillmentSettingsPage mode="payment" />} />
                  <Route path="settings/fulfillment" element={<Navigate to="/settings/shipping" replace />} />
                </Route>
                <Route element={<RequirePack anyOf={[CAPABILITY_PACK.OFFER_PROMO]} />}>
                  <Route path="settings/discount-codes" element={<DiscountCodesPage />} />
                </Route>
                <Route element={<RequireRoles anyOf={['SUPER_ADMIN', 'CATALOG']} />}>
                  <Route path="settings/data-sources" element={<ExternalCatalogDataSourcePage />} />
                </Route>
                <Route element={<RequireRoles anyOf={['SUPER_ADMIN']} />}>
                  <Route path="users/:userId" element={<UsersPage />} />
                  <Route path="users" element={<UsersPage />} />
                  <Route path="roles/:roleId" element={<RoleDetailPage />} />
                  <Route path="roles" element={<RolesPage />} />
                  <Route path="playground" element={<DesignSystemPlaygroundPage />} />
                </Route>
                <Route path="*" element={<NotFoundPage />} />
              </Route>
            </Route>
          </Routes>
        </>
      </ToastProvider>
    </AuthProvider>
  );
}
